import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Error Types
export interface AppError {
  id: string;
  type: 'network' | 'validation' | 'auth' | 'permission' | 'system' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical';
  message: string;
  details?: string;
  timestamp: number;
  source: string; // Component or service that caused the error
  stack?: string;
  userAction?: string; // What the user was doing when error occurred
  resolved: boolean;
  retryCount: number;
  maxRetries: number;
}

export interface ErrorState {
  // Error Management
  errors: AppError[];
  activeErrors: AppError[];
  errorHistory: AppError[];
  
  // Error Settings
  maxErrors: number;
  maxHistory: number;
  autoRetry: boolean;
  retryDelay: number;
  
  // Error Statistics
  errorStats: {
    total: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
    bySource: Record<string, number>;
    last24h: number;
    lastWeek: number;
  };
  
  // Actions
  addError: (error: Omit<AppError, 'id' | 'timestamp' | 'resolved' | 'retryCount'>) => void;
  removeError: (id: string) => void;
  resolveError: (id: string) => void;
  retryError: (id: string) => void;
  clearErrors: () => void;
  clearErrorHistory: () => void;
  
  // Error Handling
  handleError: (error: Error, source: string, userAction?: string) => void;
  handleNetworkError: (error: any, source: string) => void;
  handleValidationError: (error: any, source: string) => void;
  handleAuthError: (error: any, source: string) => void;
  
  // Statistics
  updateErrorStats: () => void;
  getErrorStats: () => ErrorState['errorStats'];
  
  // Settings
  updateErrorSettings: (settings: Partial<Pick<ErrorState, 'maxErrors' | 'maxHistory' | 'autoRetry' | 'retryDelay'>>) => void;
}

// Helper Functions
const generateErrorId = (): string => `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const getErrorType = (error: Error): AppError['type'] => {
  if (error.name.includes('Network') || error.message.includes('fetch')) return 'network';
  if (error.name.includes('Validation') || error.message.includes('validation')) return 'validation';
  if (error.name.includes('Auth') || error.message.includes('unauthorized')) return 'auth';
  if (error.name.includes('Permission') || error.message.includes('forbidden')) return 'permission';
  if (error.name.includes('System') || error.message.includes('system')) return 'system';
  return 'unknown';
};

const getErrorSeverity = (error: Error, type: AppError['type']): AppError['severity'] => {
  if (type === 'auth' || type === 'permission') return 'high';
  if (type === 'network' && error.message.includes('timeout')) return 'medium';
  if (type === 'system') return 'critical';
  return 'low';
};

export const useErrorStore = create<ErrorState>()(
  persist(
    (set: any, get: any) => ({
      // Initial State
      errors: [],
      activeErrors: [],
      errorHistory: [],
      
      maxErrors: 50,
      maxHistory: 200,
      autoRetry: true,
      retryDelay: 1000,
      
      errorStats: {
        total: 0,
        byType: {},
        bySeverity: {},
        bySource: {},
        last24h: 0,
        lastWeek: 0,
      },
      
      // Error Management Actions
      addError: (errorData: any) => {
        const newError: AppError = {
          ...errorData,
          id: generateErrorId(),
          timestamp: Date.now(),
          resolved: false,
          retryCount: 0,
        };
        
        set((state: any) => {
          const newErrors = [...state.errors, newError];
          const newActiveErrors = newErrors.filter(e => !e.resolved);
          
          // Limit errors
          const limitedErrors = newErrors.slice(-state.maxErrors);
          const limitedActiveErrors = newActiveErrors.slice(-state.maxErrors);
          
          return {
            errors: limitedErrors,
            activeErrors: limitedActiveErrors,
          };
        });
        
        // Update statistics
        get().updateErrorStats();
      },
      
      removeError: (id) => {
        set((state: any) => ({
          errors: state.errors.filter(e => e.id !== id),
          activeErrors: state.activeErrors.filter(e => e.id !== id),
        }));
        get().updateErrorStats();
      },
      
      resolveError: (id) => {
        set((state: any) => {
          const updatedErrors = state.errors.map(e => 
            e.id === id ? { ...e, resolved: true } : e
          );
          const updatedActiveErrors = updatedErrors.filter(e => !e.resolved);
          
          return {
            errors: updatedErrors,
            activeErrors: updatedActiveErrors,
          };
        });
        get().updateErrorStats();
      },
      
      retryError: (id) => {
        set((state: any) => {
          const updatedErrors = state.errors.map(e => 
            e.id === id ? { ...e, retryCount: e.retryCount + 1 } : e
          );
          const updatedActiveErrors = updatedErrors.filter(e => !e.resolved);
          
          return {
            errors: updatedErrors,
            activeErrors: updatedActiveErrors,
          };
        });
      },
      
      clearErrors: () => {
        set((state: any) => ({
          errors: [],
          activeErrors: [],
          errorHistory: [...state.errorHistory, ...state.errors],
        }));
        get().updateErrorStats();
      },
      
      clearErrorHistory: () => {
        set(() => ({
          errorHistory: [],
        }));
        get().updateErrorStats();
      },
      
      // Error Handling
      handleError: (error, source, userAction) => {
        const type = getErrorType(error);
        const severity = getErrorSeverity(error, type);
        
        get().addError({
          type,
          severity,
          message: error.message,
          details: error.stack,
          source,
          userAction,
          maxRetries: 3,
        });
      },
      
      handleNetworkError: (error, source) => {
        get().addError({
          type: 'network',
          severity: 'medium',
          message: error.message || 'Network error occurred',
          details: error.response?.data || error.stack,
          source,
          maxRetries: 3,
        });
      },
      
      handleValidationError: (error, source) => {
        get().addError({
          type: 'validation',
          severity: 'low',
          message: error.message || 'Validation error occurred',
          details: error.details || error.stack,
          source,
          maxRetries: 1,
        });
      },
      
      handleAuthError: (error, source) => {
        get().addError({
          type: 'auth',
          severity: 'high',
          message: error.message || 'Authentication error occurred',
          details: error.response?.data || error.stack,
          source,
          maxRetries: 1,
        });
      },
      
      // Statistics
      updateErrorStats: () => {
        const state = get();
        const now = Date.now();
        const last24h = now - (24 * 60 * 60 * 1000);
        const lastWeek = now - (7 * 24 * 60 * 60 * 1000);
        
        const allErrors = [...state.errors, ...state.errorHistory];
        
        const stats = {
          total: allErrors.length,
          byType: {} as Record<string, number>,
          bySeverity: {} as Record<string, number>,
          bySource: {} as Record<string, number>,
          last24h: allErrors.filter(e => e.timestamp >= last24h).length,
          lastWeek: allErrors.filter(e => e.timestamp >= lastWeek).length,
        };
        
        allErrors.forEach(error => {
          stats.byType[error.type] = (stats.byType[error.type] || 0) + 1;
          stats.bySeverity[error.severity] = (stats.bySeverity[error.severity] || 0) + 1;
          stats.bySource[error.source] = (stats.bySource[error.source] || 0) + 1;
        });
        
        set({ errorStats: stats });
      },
      
      getErrorStats: () => {
        return get().errorStats;
      },
      
      // Settings
      updateErrorSettings: (settings: any) => {
        set((state: any) => ({
          ...state,
          ...settings,
        }));
      },
    }),
    {
      name: 'myfood-error-store',
      partialize: (state: any) => ({
        maxErrors: state.maxErrors,
        maxHistory: state.maxHistory,
        autoRetry: state.autoRetry,
        retryDelay: state.retryDelay,
        errorHistory: state.errorHistory.slice(-50), // Keep only last 50 for persistence
      }),
    }
  )
);
